import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiUsers, FiFileText, FiList, FiCalendar, FiFile, FiUser, FiClock, FiEdit2, FiPlus } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import DocumentosSection from '../components/DocumentosSection';
import { formatCpfCnpj, formatCep } from '../utils/formatCpfCnpj';
import { buscarPorCep } from '../services/viaCep';
import { TiposProcessoOptions } from '../constants/tiposProcessoLicenciamento';
import './EmpreendimentoDetalhes.css';

/** Converte DD-MM-YYYY ou YYYY-MM-DD para exibição */
function formatarData(val) {
  if (!val) return '-';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  }
  return s;
}

const EmpreendimentoDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ nome: '', cnpj_cpf: '', municipio: '', status: 'ativo', descricao: '', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', uf: '' });
  const [cepLoading, setCepLoading] = useState(false);
  const [error, setError] = useState(null);
  const [empreendimento, setEmpreendimento] = useState(null);
  const [licencas, setLicencas] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [showProcessoModal, setShowProcessoModal] = useState(false);
  const [processoForm, setProcessoForm] = useState({ titulo: '', tipo: 'outro', orgao_ambiental: '', numero_processo: '', descricao: '', data_abertura: '', data_limite: '', status: 'preparacao' });
  const [condicionantes, setCondicionantes] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [historico, setHistorico] = useState([]);

  const carregarDetalhes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, processosRes] = await Promise.all([
        api.get(`/api/empreendimentos/${id}/detalhes`),
        api.get(`/api/processos-licenciamento?empreendimento_id=${id}`).catch(() => ({ data: [] }))
      ]);
      const data = res.data;
      setEmpreendimento(data.empreendimento);
      setLicencas(data.licencas || []);
      setProcessos(processosRes.data || []);
      setCondicionantes(data.condicionantes_monitoradas || []);
      setTarefas(data.tarefas || []);
      setUsuarios(data.usuarios || []);
      setDocumentos(data.documentos || []);
      setHistorico(data.historico || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar detalhes do empreendimento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const carregarDocumentos = useCallback(async () => {
    try {
      const res = await api.get(`/api/documentos?empreendimento_id=${id}`);
      const data = res.data;
      const list = Array.isArray(data) ? data : (data && typeof data === 'object' && 'data' in data ? (data.data || []) : []);
      setDocumentos(prev => {
        if (list.length === 0 && prev.length > 0) return prev;
        return list;
      });
    } catch (err) {
      console.error('Erro ao carregar documentos:', err);
      /* Não zerar a lista no erro para não apagar o que o usuário acabou de adicionar */
    }
  }, [id]);

  useEffect(() => {
    carregarDetalhes();
  }, [carregarDetalhes]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/empreendimentos/${id}`, editForm);
      setShowEditModal(false);
      carregarDetalhes();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar');
    }
  };

  const handleProcessoSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/processos-licenciamento', { ...processoForm, empreendimento_id: id });
      setShowProcessoModal(false);
      setProcessoForm({ titulo: '', tipo: 'outro', orgao_ambiental: '', numero_processo: '', descricao: '', data_abertura: '', data_limite: '', status: 'preparacao' });
      navigate(`/processos-licenciamento/${res.data.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar processo de licenciamento');
    }
  };

  const abrirEditar = () => {
    setEditForm({
      nome: empreendimento?.nome || '',
      cnpj_cpf: formatCpfCnpj(empreendimento?.cnpj_cpf || ''),
      municipio: empreendimento?.municipio || '',
      status: empreendimento?.status || 'ativo',
      descricao: empreendimento?.descricao || '',
      cep: formatCep(empreendimento?.cep || ''),
      logradouro: empreendimento?.logradouro || '',
      numero: empreendimento?.numero || '',
      complemento: empreendimento?.complemento || '',
      bairro: empreendimento?.bairro || '',
      uf: empreendimento?.uf || ''
    });
    setShowEditModal(true);
  };

  const handleCepBlurEdit = async () => {
    const digits = String(editForm.cep || '').replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const endereco = await buscarPorCep(editForm.cep);
      if (!endereco.erro) {
        setEditForm((prev) => ({
          ...prev,
          logradouro: endereco.logradouro || prev.logradouro,
          bairro: endereco.bairro || prev.bairro,
          municipio: endereco.localidade || prev.municipio,
          uf: endereco.uf || prev.uf
        }));
      }
    } catch (e) {
      console.warn('Busca CEP:', e);
    } finally {
      setCepLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="empreendimento-detalhes-page">
        <div className="loading" aria-busy="true">Carregando detalhes do empreendimento</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empreendimento-detalhes-page">
        <div className="error-state">
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/empreendimentos')}>
            Voltar aos empreendimentos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="empreendimento-detalhes-page">
      <header className="page-header">
        <h1 className="page-title">{empreendimento?.nome || 'Detalhes do empreendimento'}</h1>
        <div className="page-header-actions">
          {hasPermission?.('enterprise', 'edit') && (
            <button type="button" className="btn btn-secondary" onClick={abrirEditar}>
              <FiEdit2 /> Editar
            </button>
          )}
          {hasPermission?.('users', 'view') !== false && (
            <Link to={`/empreendimentos/${id}/usuarios`} className="btn btn-info">
              <FiUsers /> Gerenciar usuários
            </Link>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/empreendimentos')}>
            <FiArrowLeft /> Voltar
          </button>
        </div>
      </header>

      <div className="empreendimento-resumo-cards">
        <div className="resumo-card glass">
          <FiFileText className="resumo-card-icon" />
          <span className="resumo-card-value">{licencas.length}</span>
          <span className="resumo-card-label">Licenças</span>
        </div>
        <div className="resumo-card glass">
          <FiList className="resumo-card-icon" />
          <span className="resumo-card-value">{condicionantes.length}</span>
          <span className="resumo-card-label">Condicionantes</span>
        </div>
        <div className="resumo-card glass">
          <FiCalendar className="resumo-card-icon" />
          <span className="resumo-card-value">{tarefas.length}</span>
          <span className="resumo-card-label">Prazos</span>
        </div>
        <div className="resumo-card glass">
          <FiFile className="resumo-card-icon" />
          <span className="resumo-card-value">{documentos.length}</span>
          <span className="resumo-card-label">Documentos</span>
        </div>
      </div>

      <div className="empreendimento-info">
        <p><strong>CNPJ/CPF:</strong> {empreendimento?.cnpj_cpf || '-'}</p>
        <p><strong>Município:</strong> {empreendimento?.municipio || '-'}</p>
        <p><strong>Status:</strong> <span className={`badge badge-${empreendimento?.status || 'ativo'}`}>{empreendimento?.status || 'Ativo'}</span></p>
        {empreendimento?.descricao && <p><strong>Descrição:</strong> {empreendimento.descricao}</p>}
      </div>

      <details className="detalhes-section collapsible-section" open>
        <summary>
          <h2><FiFileText className="section-icon" /> Licenças vinculadas</h2>
          <span className="collapsible-badge">{licencas.length} licença(s)</span>
        </summary>
        <p className="section-help">Licenças ambientais vinculadas a este empreendimento.</p>
        {hasPermission?.('license', 'create') && (
          <div className="section-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/licencas?empreendimentoId=${id}`)}
            >
              <FiPlus /> Adicionar licença
            </button>
          </div>
        )}
        {licencas.length === 0 ? (
          <p className="text-muted">Nenhuma licença vinculada.</p>
        ) : (
          <div className="licencas-list">
            {licencas.map((lic) => (
              <div key={lic.id} className="licenca-item">
                <div className="licenca-item-header">
                  <Link to={`/licencas/${lic.id}/validar`} className="licenca-link">
                    {lic.tipo} {lic.numero ? `- ${lic.numero}` : ''}
                  </Link>
                  <span className="licenca-meta">
                    {lic.orgao_emissor && `${lic.orgao_emissor} · `}
                    {lic.condicionantes_count} condicionante(s)
                  </span>
                </div>
                <div className="licenca-item-dates">
                  {lic.data_emissao && <span>Emissão: {formatarData(lic.data_emissao)}</span>}
                  {lic.data_vencimento && <span>Vencimento: {formatarData(lic.data_vencimento)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </details>

      <details className="detalhes-section collapsible-section" open>
        <summary>
          <h2><FiFileText className="section-icon" /> Processos de licenciamento</h2>
          <span className="collapsible-badge">{processos.length} processo(s)</span>
        </summary>
        <p className="section-help">Planejamento, protocolo e acompanhamento antes da emissão da licença.</p>
        {hasPermission?.('license', 'create') && (
          <div className="section-actions">
            <button type="button" className="btn btn-primary" onClick={() => setShowProcessoModal(true)}>
              <FiPlus /> Criar processo
            </button>
          </div>
        )}
        {processos.length === 0 ? <p className="text-muted">Nenhum processo cadastrado.</p> : (
          <div className="licencas-list">
            {processos.map((p) => (
              <div key={p.id} className="licenca-item">
                <div className="licenca-item-header">
                  <Link to={`/processos-licenciamento/${p.id}`} className="licenca-link">{p.titulo}</Link>
                  <span className="licenca-meta">{p.status.replace(/_/g, ' ')} · {p.tarefas_count} prazo(s) · {p.documentos_count} documento(s)</span>
                </div>
                <div className="licenca-item-dates">
                  <span>Número: {p.numero_processo || 'a informar'}</span>
                  {p.data_limite && <span>Prazo: {formatarData(p.data_limite)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </details>

      <details className="detalhes-section collapsible-section">
        <summary>
          <h2><FiList className="section-icon" /> Condicionantes monitoradas</h2>
          <span className="collapsible-badge">{condicionantes.length} condicionante(s)</span>
        </summary>
        <p className="section-help">Condicionantes que exigem monitoramento, das licenças vinculadas.</p>
        {condicionantes.length === 0 ? (
          <p className="text-muted">Nenhuma condicionante com monitoramento ativo.</p>
        ) : (
          <div className="condicionantes-monitoradas-list">
            {condicionantes.map((c) => (
              <div key={c.id} className="condicionante-monitorada-item">
                <div className="condicionante-monitorada-header">
                  <span className="condicionante-monitorada-num">{c.condicionante_numero ?? '-'}</span>
                  <span className="condicionante-monitorada-licenca">{c.licenca_tipo} {c.licenca_numero || ''}</span>
                  <span className="condicionante-monitorada-tema">{c.tema || 'Sem tema'}</span>
                </div>
                <p className="condicionante-monitorada-resumo">
                  {(c.descricao || '').substring(0, 200)}
                  {(c.descricao || '').length > 200 ? '…' : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </details>

      <details className="detalhes-section collapsible-section">
        <summary>
          <h2><FiCalendar className="section-icon" /> Prazos</h2>
          <span className="collapsible-badge">{tarefas.length} prazo(s)</span>
        </summary>
        <p className="section-help">Prazos de monitoramento das licenças vinculadas.</p>
        {tarefas.length === 0 ? (
          <p className="text-muted">Nenhum prazo cadastrado.</p>
        ) : (
          <div className="prazos-table-wrapper">
            <table className="prazos-table">
              <thead>
                <tr>
                  <th>Licença</th>
                  <th>Condicionante</th>
                  <th>Título</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Responsável</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tarefas.map((t) => (
                  <tr key={t.id}>
                    <td>{t.licenca_tipo} {t.licenca_numero || '-'}</td>
                    <td>
                      <span className="prazos-cond-num">{t.condicionante_numero ?? '-'}</span>
                      {t.tema && <span className="prazos-cond-tema"> {t.tema}</span>}
                    </td>
                    <td>{t.titulo || '-'}</td>
                    <td>{formatarData(t.data_vencimento)}</td>
                    <td>
                      <span className={`badge-status badge-${(t.status || 'pendente').replace(/_/g, '-')}`}>
                        {(t.status || 'pendente').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{t.responsavel_nome || '-'}</td>
                    <td>
                      <Link to="/tarefas/kanban" className="btn btn-sm btn-secondary">Ver no Kanban</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>

      <details className="detalhes-section collapsible-section">
        <summary>
          <h2><FiFile className="section-icon" /> Documentos vinculados</h2>
          <span className="collapsible-badge">{documentos.length} documento(s)</span>
        </summary>
        <p className="section-help">Documentos anexados a este empreendimento.</p>
        <DocumentosSection empreendimentoId={id} documentos={documentos} onRefresh={carregarDocumentos} onDocumentAdded={(doc) => setDocumentos(prev => [...prev, doc])} />
      </details>

      <details className="detalhes-section collapsible-section">
        <summary>
          <h2><FiUser className="section-icon" /> Usuários vinculados</h2>
          <span className="collapsible-badge">{usuarios.length} usuário(s)</span>
        </summary>
        <p className="section-help">Usuários com acesso a este empreendimento.</p>
        {usuarios.length === 0 ? (
          <p className="text-muted">Nenhum usuário vinculado.</p>
        ) : (
          <div className="usuarios-table-wrapper">
            <table className="usuarios-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Papel</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.id}>
                    <td>{u.nome || '-'}</td>
                    <td>{u.email || '-'}</td>
                    <td>{u.role_nome || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>

      <details className="detalhes-section collapsible-section">
        <summary>
          <h2><FiClock className="section-icon" /> Histórico</h2>
          <span className="collapsible-badge">{historico.length} registro(s)</span>
        </summary>
        <p className="section-help">Histórico de alterações em licenças e usuários vinculados ao empreendimento (criação, edição e exclusão).</p>
        {historico.length === 0 ? (
          <p className="text-muted">Nenhum registro no histórico.</p>
        ) : (
          <div className="historico-list">
            {historico.map((h) => (
              <details key={h.id} className="historico-item">
                <summary>
                  <span className="historico-data">{new Date(h.criado_em).toLocaleString('pt-BR')}</span>
                  <span className="historico-usuario">{h.usuario_nome || 'Sistema'}</span>
                  <span className="historico-descricao">
                    {h.descricao || `${h.acao} em ${h.entidade}`}
                    {h.licenca_numero && ` (Lic. ${h.licenca_numero})`}
                  </span>
                  <span className="historico-entidade">{h.entidade}</span>
                </summary>
                <pre className="debug-pre">
                  {(() => {
                    const parse = (v) => {
                      if (!v) return null;
                      if (typeof v === 'object') return v;
                      try { return JSON.parse(v); } catch (_) { return v; }
                    };
                    const fmt = (v) => (typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v));
                    const antes = parse(h.dados_anteriores);
                    const depois = parse(h.dados_novos);
                    if (antes && depois) return `Antes: ${fmt(antes)}\n\nDepois: ${fmt(depois)}`;
                    if (antes) return `Antes: ${fmt(antes)}`;
                    if (depois) return `Depois: ${fmt(depois)}`;
                    return '(sem detalhes)';
                  })()}
                </pre>
              </details>
            ))}
          </div>
        )}
      </details>

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2><FiEdit2 /> Editar Empreendimento</h2>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  required
                  value={editForm.nome}
                  onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>CNPJ/CPF</label>
                <input
                  type="text"
                  value={editForm.cnpj_cpf}
                  onChange={(e) => setEditForm({ ...editForm, cnpj_cpf: formatCpfCnpj(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>CEP</label>
                <input
                  type="text"
                  value={editForm.cep}
                  onChange={(e) => setEditForm({ ...editForm, cep: formatCep(e.target.value) })}
                  onBlur={handleCepBlurEdit}
                  placeholder="00000-000"
                  maxLength={9}
                />
                {cepLoading && <span className="form-help">Buscando endereço...</span>}
                <span className="form-help">Preencha o CEP para buscar Estado, Cidade e Endereço.</span>
              </div>
              {(String(editForm.cep || '').replace(/\D/g, '').length === 8 || editForm.logradouro || editForm.uf) && (
                <div className="form-address-block">
                  <div className="form-group">
                    <label>Estado (UF)</label>
                    <input
                      type="text"
                      value={editForm.uf}
                      onChange={(e) => setEditForm({ ...editForm, uf: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Cidade</label>
                    <input
                      type="text"
                      value={editForm.municipio}
                      onChange={(e) => setEditForm({ ...editForm, municipio: e.target.value })}
                      placeholder="Município"
                    />
                  </div>
                  <div className="form-group">
                    <label>Endereço</label>
                    <input
                      type="text"
                      value={editForm.logradouro}
                      onChange={(e) => setEditForm({ ...editForm, logradouro: e.target.value })}
                      placeholder="Rua, avenida..."
                    />
                  </div>
                  <div className="form-group form-group-inline">
                    <div className="form-group">
                      <label>Número</label>
                      <input
                        type="text"
                        value={editForm.numero}
                        onChange={(e) => setEditForm({ ...editForm, numero: e.target.value })}
                        placeholder="Nº ou km"
                      />
                    </div>
                    <div className="form-group">
                      <label>Complemento</label>
                      <input
                        type="text"
                        value={editForm.complemento}
                        onChange={(e) => setEditForm({ ...editForm, complemento: e.target.value })}
                        placeholder="Sala, bloco..."
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                  <option value="suspenso">Suspenso</option>
                </select>
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={editForm.descricao}
                  onChange={(e) => setEditForm({ ...editForm, descricao: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showProcessoModal && (
        <div className="modal-overlay" onClick={() => setShowProcessoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Criar processo de licenciamento</h2>
            <form onSubmit={handleProcessoSubmit}>
              <div className="form-group"><label>Título *</label><input required value={processoForm.titulo} onChange={(e) => setProcessoForm({ ...processoForm, titulo: e.target.value })} /></div>
              <div className="form-group"><label>Tipo</label><select value={processoForm.tipo} onChange={(e) => setProcessoForm({ ...processoForm, tipo: e.target.value })}>{TiposProcessoOptions()}</select></div>
              <div className="form-group"><label>Órgão ambiental</label><input value={processoForm.orgao_ambiental} onChange={(e) => setProcessoForm({ ...processoForm, orgao_ambiental: e.target.value })} /></div>
              <div className="form-group"><label>Número do processo</label><input value={processoForm.numero_processo} onChange={(e) => setProcessoForm({ ...processoForm, numero_processo: e.target.value })} /></div>
              <div className="form-group"><label>Data de abertura</label><input type="date" value={processoForm.data_abertura} onChange={(e) => setProcessoForm({ ...processoForm, data_abertura: e.target.value })} /></div>
              <div className="form-group"><label>Prazo</label><input type="date" value={processoForm.data_limite} onChange={(e) => setProcessoForm({ ...processoForm, data_limite: e.target.value })} /></div>
              <div className="form-group"><label>Descrição</label><textarea rows="3" value={processoForm.descricao} onChange={(e) => setProcessoForm({ ...processoForm, descricao: e.target.value })} /></div>
              <div className="form-actions"><button type="button" className="btn btn-secondary" onClick={() => setShowProcessoModal(false)}>Cancelar</button><button className="btn btn-primary">Criar processo</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpreendimentoDetalhes;
